import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import type { Options, Root } from 'rehype-sanitize'

// Keep the library's protocol checks and DOM-clobbering protection. Extend
// formatting only for the classes emitted by highlight.ts and ErrorSection.
export const cmsHtmlSchema: Options = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), 'u'],
    strip: [
        ...(defaultSchema.strip || []),
        'iframe',
        'object',
        'embed',
        'style',
        'svg',
        'math',
    ],
    attributes: {
        ...defaultSchema.attributes,
        '*': (defaultSchema.attributes?.['*'] || []).filter(
            (attribute) => attribute !== 'target' && attribute !== 'rel'
        ),
        a: [
            ...(defaultSchema.attributes?.a || []),
            ['target', '_blank', '_self'],
            ['className', 'text-primary-500', 'font-semibold', 'underline'],
        ],
        strong: [
            ...(defaultSchema.attributes?.strong || []),
            ['className', 'text-primary-500', 'font-inherit'],
        ],
    },
    required: {
        ...defaultSchema.required,
        a: { rel: ['noopener', 'noreferrer'] },
    },
    protocols: {
        ...defaultSchema.protocols,
        href: ['http', 'https', 'mailto', 'tel'],
        src: ['http', 'https'],
    },
}

type CmsNode = Root | Root['children'][number]

// Keep in-document links working after the sanitizer prefixes ids/names.
// This only rewrites fragments to targets already present in the cleaned tree.
function preserveCmsFragments() {
    return (tree: Root) => {
        const targets = new Set<string>()
        const prefix = defaultSchema.clobberPrefix || ''

        function visit(node: CmsNode, callback: (node: CmsNode) => void) {
            callback(node)
            if ('children' in node) {
                node.children.forEach((child) => visit(child, callback))
            }
        }

        visit(tree, (node) => {
            if (node.type !== 'element') return
            for (const key of ['id', 'name']) {
                const value = node.properties?.[key]
                if (typeof value === 'string') targets.add(value)
            }
        })

        visit(tree, (node) => {
            if (
                node.type !== 'element' ||
                node.tagName !== 'a' ||
                !node.properties
            )
                return
            const href = node.properties.href
            if (typeof href !== 'string' || !href.startsWith('#')) return
            let fragment: string
            try {
                fragment = decodeURIComponent(href.slice(1))
            } catch {
                // Leave malformed escapes untouched rather than guessing a target.
                return
            }
            const target = prefix + fragment
            if (targets.has(target)) node.properties.href = '#' + target
        })
    }
}

// Sanitize AFTER parsing raw HTML. The final plugin only repairs safe fragments.
export const cmsRehypePlugins: [
    typeof rehypeRaw,
    [typeof rehypeSanitize, Options],
    typeof preserveCmsFragments
] = [rehypeRaw, [rehypeSanitize, cmsHtmlSchema], preserveCmsFragments]
